import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {
  AdminTranslationsApi,
  type Translation,
  type Translations,
} from "mws-catalog-ts-sdk-v2-admin";
import { type MaybeRefOrGetter, toValue } from "vue";

import type { UseQueryOptionsOmitQueryKey } from "@/common/types";

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;
const adminTranslationsApi = new AdminTranslationsApi(undefined, catalogApiUrl);

/**
 * Transforms translation values keys from camelCase back to original dash format.
 * The axios camelCase transformer converts "nl-NL" to "nlNl", this reverses that.
 */
export const transformTranslationKeys = (
  translations: Translations,
): Translations => {
  const transformedValues: Record<string, Translation> = {};

  for (const [key, value] of Object.entries(translations.values)) {
    transformedValues[
      `${key.substring(0, 2)}-${key.substring(2, 4).toUpperCase()}`
    ] = value;
  }

  return { ...translations, values: transformedValues };
};

export const useEntityTranslationsQuery = (
  entity: MaybeRefOrGetter<string>,
  entityId: MaybeRefOrGetter<string>,
  key: MaybeRefOrGetter<string>,
  options?: UseQueryOptionsOmitQueryKey<Translations>,
) => {
  const queryKey = ["translations", entity, entityId, key];

  const queryFn = async () => {
    const response =
      await adminTranslationsApi.v2AdminTranslationsEntityEntityIdKeyGet({
        entityId: toValue(entityId),
        key: toValue(key),
      });

    return transformTranslationKeys(response.data);
  };

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  });
};

export const useUpdateEntityTranslationMutation = (
  entity: MaybeRefOrGetter<string>,
  entityId: MaybeRefOrGetter<string>,
  translationKey: MaybeRefOrGetter<string>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { language: string; value: string }) => {
      await axios.put(
        `${catalogApiUrl}/v2/admin/translations/entity/${toValue(entityId)}`,
        {
          key: toValue(translationKey),
          language: request.language,
          value: request.value,
          isAiGenerated: false,
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "translations",
          toValue(entity),
          toValue(entityId),
          toValue(translationKey),
        ],
      });
    },
  });
};
